import { CreditScoreCard, DataPoint } from 'components'
import { useCreditContext } from 'contexts'

export const CreditScore = () => {
  const { creditScore } = useCreditContext()
  const history = creditScore?.history || []

  let sortedByEpoch = history.sort((a, b) => a.timestamp - b.timestamp)

  if (sortedByEpoch.length > 0) {
    sortedByEpoch = sortedByEpoch.slice(0, -1) // Drop upcoming epoch
  }

  let chartData: DataPoint[] = sortedByEpoch.map((entry) => ({
    timestamp: entry.timestamp,
    score: parseInt(entry.score),
  }))

  const currentScore = creditScore.simpleScore?.score ?? '0'

  let changeLastDay: number
  if (chartData.length === 0) {
    changeLastDay = 0
  } else if (chartData.length === 1) {
    changeLastDay = chartData[0].score
  } else {
    changeLastDay = chartData[chartData.length - 1].score - chartData[chartData.length - 2].score
  }

  if (chartData.length > 10) {
    // If more than 10 points, average out to 10 points.
    chartData = averageChartData(10, chartData)
  } else if (chartData.length < 10 && chartData.length > 0) {
    // If less than 10 points, pad with empty data points.
    chartData = Array(10 - chartData.length)
      .fill({ timestamp: chartData[0].timestamp, score: 0 })
      .concat(chartData)
  }

  return (
    <CreditScoreCard
      aboutCreditScoreUrl="https://wiki.arcx.money/application/defi-credit-score"
      maxScore={1000}
      tooltip="Represents creditworthiness and determines capital efficiency on ARCx Credit."
      last24hChange={changeLastDay}
      chartData={chartData}
      currentScore={currentScore ? parseInt(currentScore.toString()) : null}
      loading={!creditScore}
    />
  )
}

/**
 * Returns an averaged set of data points given in `chartData`, excluding the last point.
 * Last point is maintained.
 * @param nrOfPoints The number of points to average over.
 * @param chartData The data points to modify.
 */
function averageChartData(nrOfPoints: number, chartData: DataPoint[]): DataPoint[] {
  const chunkSize = Math.floor(chartData.length / nrOfPoints)
  const chunks: DataPoint[][] = []
  const lastPoint = chartData[chartData.length - 1]

  for (let i = 0; i < nrOfPoints - 2; i++) {
    chunks.push(chartData.slice(i * chunkSize, i * chunkSize + chunkSize))
  }
  // The second last batch includes the remainder
  chunks.push(chartData.slice((nrOfPoints - 1) * chunkSize, chartData.length - 1))

  // Store the averages in chartData
  chartData = []
  chunks.forEach((chunk) => {
    const { scoreSum, timeSum } = chunk.reduce(
      (acc, curr) => ({
        scoreSum: acc.scoreSum + curr.score,
        timeSum: acc.timeSum + curr.timestamp,
      }),
      { scoreSum: 0, timeSum: 0 },
    )

    chartData.push({
      score: scoreSum / chunk.length,
      timestamp: timeSum / chunk.length,
    })
  })

  // Maintain the last point in chart
  chartData.push(lastPoint)

  return chartData
}
